import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
  Drawer,
  Divider,
  TextField,
} from "@mui/material";
import { Close, CheckCircle } from "@mui/icons-material";

const MarkDoneSidebar = ({
  open,
  onClose,
  task,
  notes,
  onNotesChange,
  onConfirm,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
          borderRadius: "16px 0 0 16px",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.1)",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Sidebar Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "#D1FAE5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircle sx={{ fontSize: 22, color: "#10B981" }} />
            </Box>
            <Typography
              sx={{ fontSize: 18, fontWeight: 600, color: "#1f2937" }}
            >
              Mark as Done
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6" },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Sidebar Content */}
        <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
          {task && (
            <>
              {/* Task Info */}
              <Box
                sx={{
                  bgcolor: "#F8FAFC",
                  borderRadius: "12px",
                  p: 2.5,
                  mb: 3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#9CA3AF",
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    mb: 1.5,
                  }}
                >
                  Task Details
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                      Client
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 500, color: "#1f2937" }}
                    >
                      {task.client || task.clientName}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                      Task
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 500, color: "#1f2937" }}
                    >
                      {task.task || task.taskType}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                      Due Time
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 500, color: "#1f2937" }}
                    >
                      {task.time || task.dueTime}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                      Priority
                    </Typography>
                    <Chip
                      label={task.priority}
                      size="small"
                      sx={{
                        bgcolor:
                          task.priorityBg ||
                          (task.priority === "HIGH" || task.priority === "High"
                            ? "#FEE2E2"
                            : "#FEF3C7"),
                        color:
                          task.priorityColor ||
                          (task.priority === "HIGH" || task.priority === "High"
                            ? "#DC2626"
                            : "#D97706"),
                        fontWeight: 600,
                        fontSize: 10,
                        height: 22,
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Notes Section */}
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#9CA3AF",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  mb: 1.5,
                }}
              >
                Completion Notes (Optional)
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                placeholder="Add any notes about this task completion..."
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    fontSize: 14,
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#CBD5E1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#10B981",
                    },
                  },
                }}
              />
            </>
          )}
        </Box>

        {/* Sidebar Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #E5E7EB",
            bgcolor: "#F8FAFC",
            display: "flex",
            gap: 2,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontWeight: 500,
              color: "#6B7280",
              borderColor: "#E5E7EB",
              borderRadius: "10px",
              py: 1.25,
              "&:hover": {
                borderColor: "#CBD5E1",
                bgcolor: "#F3F4F6",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onConfirm}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "#10B981",
              borderRadius: "10px",
              py: 1.25,
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
              "&:hover": {
                bgcolor: "#059669",
              },
            }}
          >
            Mark as Done
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MarkDoneSidebar;
